import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export const isDev = () => {
  if (typeof window === "undefined") return false;

  return ["localhost", "127.0.0.1", "0.0.0.0"].includes(
    window.location.hostname,
  );
};

export const getBoundingBoxStyle = (points: number[][]) => {
  if (points.length === 0) {
    return {
      left: "0%",
      top: "0%",
      width: "0%",
      height: "0%",
    };
  }
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [x, y] of points) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return {
    left: minX * 100 + "%",
    top: minY * 100 + "%",
    width: (maxX - minX) * 100 + "%",
    height: (maxY - minY) * 100 + "%",
  };
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const QUERY_IMAGE_CONTROL_ICON_SIZE = 14;

// Below this (in seconds) two capture times are treated as effectively equal.
const RELATIVE_AGE_SAME_THRESHOLD_S = 60;

const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = hours / 24;
  return `${Math.round(days)}d`;
};

/**
 * Human-readable age of a reference/shelf image relative to the query image.
 * Shelf images are always captured before the query, so we only ever label
 * "2d before" / "5h before" / "same time".
 * Returns null when either timestamp is missing or unparseable, or in the
 * unexpected case where the shelf image is newer than the query (we'd rather
 * show nothing than a misleading badge).
 */
export const formatRelativeAge = (
  referenceTimestamp: string | undefined,
  queryTimestamp: string | undefined,
): string | null => {
  if (!referenceTimestamp || !queryTimestamp) return null;
  const refMs = new Date(referenceTimestamp).getTime();
  const queryMs = new Date(queryTimestamp).getTime();
  if (Number.isNaN(refMs) || Number.isNaN(queryMs)) return null;

  const deltaSeconds = (refMs - queryMs) / 1000;
  if (Math.abs(deltaSeconds) < RELATIVE_AGE_SAME_THRESHOLD_S) {
    return "same time";
  }
  if (deltaSeconds > 0) return null;
  return `${formatDuration(Math.abs(deltaSeconds))} before`;
};
